import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

/**
 * Session timeout detection and handling service
 * 
 * This interceptor catches 403 and 401 errors globally and triggers
 * the session timeout modal when appropriate.
 */

// Prevent duplicate modals
let isSessionTimeoutModalOpen = false;

// Callback function to trigger modal (will be set during setup)
let triggerSessionTimeoutCallback: (() => void) | null = null;

/**
 * Response interceptor to catch authentication errors
 */
const responseInterceptor = axios.interceptors.response.use(
  // Pass through successful responses
  (response) => response,
  
  // Handle errors
  (error: AxiosError) => {
    // Check if it's an authentication error (401 or 403)
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      console.log('[Axios Interceptor] Authentication error detected:', {
        status: error.response.status,
        url: error.config?.url,
        data: error.response.data,
      });

      // Check if this is a session timeout (not just a permission denied)
      const isSessionTimeout = checkIfSessionTimeout(error);

      if (isSessionTimeout && !isSessionTimeoutModalOpen) {
        console.log('[Axios Interceptor] Triggering session timeout modal');
        isSessionTimeoutModalOpen = true;
        
        // Trigger the modal
        if (triggerSessionTimeoutCallback) {
          triggerSessionTimeoutCallback();
        }
      }
    }

    // Always reject the error so calling code can handle it too
    return Promise.reject(error);
  }
);

/**
 * Check if the error is a session timeout vs a permission denied
 */
function checkIfSessionTimeout(error: AxiosError): boolean {
  const responseData = error.response?.data as any;
  
  // Check for specific error messages that indicate session timeout
  if (responseData?.message) {
    const message = responseData.message.toLowerCase();
    
    // Common session timeout messages
    const sessionTimeoutKeywords = [
      'session expired',
      'token expired',
      'invalid token',
      'unauthorized',
      'authentication failed',
      'forbidden resource',
    ];
    
    const isTimeout = sessionTimeoutKeywords.some(keyword => message.includes(keyword));
    
    if (isTimeout) {
      return true;
    }
  }

  // For 401, always treat as session timeout (unauthorized)
  if (error.response?.status === 401) {
    return true;
  }

  // For 403, check if we have a token (if we do, it's expired; if not, it's permission denied)
  if (error.response?.status === 403) {
    const token = localStorage.getItem('authToken');
    return !!token; // If token exists, it's likely expired
  }

  return false;
}

/**
 * Setup function to initialize the interceptor
 * Must be called once during app initialization
 */
export function setupAxiosInterceptor(triggerCallback: () => void): void {
  console.log('[Axios Interceptor] Setting up interceptor');
  triggerSessionTimeoutCallback = triggerCallback;
}

/**
 * Reset the modal state (called when modal is closed or user logs out)
 */
export function resetSessionTimeoutState(): void {
  console.log('[Axios Interceptor] Resetting session timeout state');
  isSessionTimeoutModalOpen = false;
}

/**
 * Request interceptor to add auth token to all requests
 */
axios.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('authToken');
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default {
  setupAxiosInterceptor,
  resetSessionTimeoutState,
};
